<template>
    <div class="login-page">
        <!-- Left: Visual -->
        <div class="login-visual">
            <div class="login-visual-overlay"></div>
            <div class="login-visual-content">
                <h2 class="login-visual-title">{{ $t('auth.reset_password.title') }}</h2>
                <p class="login-visual-text">
                    Choisissez un nouveau mot de passe sécurisé
                </p>
            </div>
        </div>

        <!-- Right: Form -->
        <div class="login-form-side">
            <div class="login-form-wrapper">
                <div class="login-form-logo">
                    <img src="@/assets/logo.jpg" alt="Logo" class="login-mobile-logo" />
                </div>
                <h1 class="login-title">{{ $t('auth.reset_password.title') }}</h1>
                <p class="login-subtitle">
                    Saisissez votre email et votre nouveau mot de passe
                </p>

                <form @submit.prevent="resetPwd">
                    <div class="form-group">
                        <label for="email">{{ $t('auth.email') }}</label>
                        <input
                            id="email"
                            v-model="email"
                            autocomplete="email"
                            autofocus
                            placeholder="votre@email.com"
                            required
                            type="email"
                        />
                        <span v-if="isSubmit && mailMessage" class="form-error">
                            {{ mailMessage }}
                        </span>
                    </div>

                    <div class="form-group">
                        <label for="password">
                            {{ $t('auth.reset_password.password') }}
                        </label>
                        <div class="password-wrapper">
                            <input
                                id="password"
                                v-model="password"
                                autocomplete="new-password"
                                :placeholder="'••••••••'"
                                required
                                :type="showPassword ? 'text' : 'password'"
                            />
                            <i
                                :class="showPassword ? 'fal fa-eye-slash' : 'fal fa-eye'"
                                class="password-toggle"
                                @click="showPassword = !showPassword"
                            ></i>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="confirmPassword">
                            {{ $t('auth.reset_password.confirm_password') }}
                        </label>
                        <div class="password-wrapper">
                            <input
                                id="confirmPassword"
                                v-model="confirmPassword"
                                autocomplete="new-password"
                                :placeholder="'••••••••'"
                                required
                                :type="showConfirm ? 'text' : 'password'"
                            />
                            <i
                                :class="showConfirm ? 'fal fa-eye-slash' : 'fal fa-eye'"
                                class="password-toggle"
                                @click="showConfirm = !showConfirm"
                            ></i>
                        </div>
                        <span v-if="isSubmit && passwordMessage" class="form-error">
                            {{ passwordMessage }}
                        </span>
                    </div>

                    <div v-if="successMessage" class="form-success">
                        <i class="fal fa-check-circle"></i>
                        {{ successMessage }}
                    </div>

                    <button type="submit" class="btn-submit" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm" role="status"></span>
                        {{ $t('auth.reset_password.submit') }}
                    </button>

                    <div class="back-link">
                        <router-link to="/sanctum">
                            <i class="fal fa-long-arrow-left mr-1"></i>
                            Retour à la connexion
                        </router-link>
                    </div>
                </form>
            </div>
        </div>

        <Toaster ref="toaster" />
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions } from 'vuex'
import Toaster from '@/components/UI/Helper/Toaster'

export default {
    name: 'PasswordReset',
    components: { Toaster },
    data: function () {
        return {
            email: '',
            password: '',
            confirmPassword: '',
            isSubmit: false,
            loading: false,
            mailMessage: '',
            passwordMessage: '',
            successMessage: '',
            showPassword: false,
            showConfirm: false,
            mailregExp:
                /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
            tokenResetPwd: ''
        }
    },
    computed: {
        errorTitle() {
            return this.$t('error.title')
        }
    },
    created() {
        this.tokenResetPwd = this.$route.params.token
    },
    methods: {
        ...mapActions('auth', ['resetPassword']),
        resetPwd() {
            this.isSubmit = true
            this.validMail(this.email)
            this.validPassword(this.password, this.confirmPassword)
            if (this.mailMessage || this.passwordMessage) return

            this.loading = true
            this.resetPassword({
                email: this.email,
                password: this.password,
                token_reset_password: this.tokenResetPwd
            })
                .then(() => {
                    this.successMessage = this.$t(
                        'auth.reset_password.success_message'
                    )
                })
                .catch((error) => {
                    error.title = this.errorTitle
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.loading = false
                    if (this.successMessage) {
                        setTimeout(() => this.$router.push('/sanctum'), 1200)
                    }
                })
        },
        validPassword(pwd, confirmpwd) {
            if (!pwd) {
                this.passwordMessage = this.$t('error.validation.required')
            } else if (pwd !== confirmpwd) {
                this.passwordMessage = this.$t(
                    'error.validation.password_mismatch'
                )
            } else {
                this.passwordMessage = ''
            }
        },
        validMail(email) {
            if (!email) {
                this.mailMessage = this.$t('error.validation.required')
            } else if (!this.mailregExp.test(email)) {
                this.mailMessage = this.$t('error.validation.mail_valid')
            } else {
                this.mailMessage = ''
            }
        }
    }
}
</script>

<style scoped>
/* ── Layout ── */
.login-page {
    position: fixed;
    inset: 0;
    display: flex;
    font-family: var(--v2-font-sans, 'Inter', system-ui, -apple-system, sans-serif);
}

/* ── Left: Visual ── */
.login-visual {
    width: 50%;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: flex-end;
}

.login-visual::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url('/img/img20.jpg') center / cover no-repeat;
    animation: slowZoom 15s ease-in-out infinite alternate;
}

@keyframes slowZoom {
    0% { transform: scale(1); }
    100% { transform: scale(1.1); }
}

.login-visual-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        135deg,
        rgba(8, 145, 178, 0.35) 0%,
        rgba(15, 23, 42, 0.55) 45%,
        rgba(15, 23, 42, 0.9) 100%
    );
}

.login-visual-content {
    position: relative;
    z-index: 1;
    padding: 0 56px 56px;
}

.login-visual-title {
    color: #fff;
    font-size: 2.4rem;
    font-weight: 700;
    margin-bottom: 8px;
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.login-visual-text {
    color: rgba(255, 255, 255, 0.72);
    font-size: 1rem;
    margin: 0;
    letter-spacing: 0.005em;
}

/* ── Right: Form ── */
.login-form-side {
    width: 50%;
    background: var(--v2-surface-2, #f1f5f9);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
}

.login-form-wrapper {
    width: 100%;
    max-width: 420px;
    background: var(--v2-surface-0, #fff);
    border: 1px solid var(--v2-border, #e2e8f0);
    border-radius: var(--v2-radius-xl, 16px);
    padding: 36px;
    box-shadow: var(--v2-shadow-lg, 0 8px 24px rgba(15, 23, 42, 0.08));
    box-sizing: border-box;
}

.login-form-wrapper form {
    width: 100%;
}

.login-form-logo {
    text-align: center;
    margin-bottom: 28px;
}

.login-mobile-logo {
    max-height: 56px;
    display: block;
    margin: 0 auto;
}

.login-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--v2-text-1, #0f172a);
    margin: 0 0 4px;
    letter-spacing: -0.02em;
    text-align: center;
}

.login-subtitle {
    color: var(--v2-text-3, #64748b);
    font-size: 0.88rem;
    margin: 0 0 28px;
    text-align: center;
}

/* ── Form ── */
.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v2-text-3, #64748b);
    margin-bottom: 8px;
}

.form-group input {
    display: block;
    width: 100%;
    padding: 12px 14px;
    border: 1px solid var(--v2-border, #e2e8f0);
    border-radius: var(--v2-radius-md, 8px);
    font-size: 0.95rem;
    color: var(--v2-text-1, #0f172a);
    background: var(--v2-surface-0, #fff);
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    box-sizing: border-box;
}

.password-wrapper {
    position: relative;
}

.password-wrapper input {
    padding-right: 44px;
}

.password-toggle {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--v2-text-4, #94a3b8);
    cursor: pointer;
    font-size: 1rem;
    transition: color 0.15s;
}

.password-toggle:hover {
    color: var(--v2-primary, #0891b2);
}

.form-group input::placeholder {
    color: var(--v2-text-4, #94a3b8);
}

.form-group input:focus {
    border-color: var(--v2-primary, #0891b2);
    box-shadow: var(--v2-shadow-focus, 0 0 0 3px rgba(8, 145, 178, 0.2));
}

.form-error {
    display: block;
    color: var(--v2-danger, #ef4444);
    font-size: 0.8rem;
    margin-top: 6px;
}

.form-success {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--v2-success, #10b981);
    font-size: 0.88rem;
    font-weight: 500;
    padding: 12px 14px;
    background: var(--v2-success-soft, rgba(16, 185, 129, 0.1));
    border: 1px solid var(--v2-success, #10b981);
    border-radius: var(--v2-radius-md, 8px);
    margin-bottom: 20px;
}

.form-success i {
    font-size: 1rem;
}

/* ── Button ── */
.btn-submit {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    background: var(--v2-primary, #0891b2);
    border: none;
    border-radius: var(--v2-radius-md, 8px);
    color: #fff;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.btn-submit:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.25);
    transform: translateY(-1px);
}

.btn-submit:active:not(:disabled) {
    transform: translateY(0);
}

.btn-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* ── Links ── */
.back-link {
    text-align: center;
    margin-top: 24px;
}

.back-link a {
    color: var(--v2-text-3, #64748b);
    font-size: 0.88rem;
    text-decoration: none !important;
    transition: color 0.15s;
}

.back-link a:hover {
    color: var(--v2-primary, #0891b2);
}

/* ── Responsive ── */
@media (max-width: 860px) {
    .login-page {
        flex-direction: column;
    }

    .login-visual {
        width: 100%;
        min-height: 220px;
    }

    .login-visual-content {
        padding: 0 32px 32px;
    }

    .login-visual-title {
        font-size: 1.4rem;
    }

    .login-form-side {
        width: 100%;
        padding: 32px 24px;
    }

    .login-form-wrapper {
        padding: 28px;
    }
}
</style>
